import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Plus, Send, Trash2 } from 'lucide-react';
import {
  createSchedule,
  deleteSchedule,
  fetchSchedules,
  sendScheduleNow,
  updateSchedule,
} from '../api';

const FREQ_LABELS = {
  manual: 'Manual only',
  daily: 'Daily',
  weekly: 'Weekly',
  hourly: 'Hourly',
  instant: 'Instant (on detection)',
};

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function ScheduleRow({ sched, onChange, onDelete, onSendNow, saving }) {
  const isNew = sched._new;
  const isHourly = sched.frequency === 'hourly';
  const isWeekly = sched.frequency === 'weekly';
  const showTime = ['daily', 'weekly'].includes(sched.frequency);

  return (
    <div className="report-schedule">
      <div className="report-schedule__row">
        <input
          type="checkbox"
          checked={Boolean(sched.enabled)}
          onChange={(e) => onChange({ ...sched, enabled: e.target.checked })}
          title="Enable this schedule"
          className="report-schedule__check"
        />
        <input
          type="email"
          placeholder="Email address"
          value={sched.recipient}
          onChange={(e) => onChange({ ...sched, recipient: e.target.value })}
          className="report-schedule__email"
        />
        <button
          type="button"
          className="icon-btn icon-btn--xs icon-btn--danger"
          onClick={() => onDelete(sched)}
          title="Remove"
        >
          <Trash2 size={11} />
        </button>
      </div>

      <div className="report-schedule__row">
        <select
          value={sched.frequency}
          onChange={(e) => onChange({ ...sched, frequency: e.target.value })}
          className="report-schedule__select"
        >
          {Object.entries(FREQ_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {isHourly && (
          <select
            value={sched.interval_hrs}
            onChange={(e) => onChange({ ...sched, interval_hrs: Number(e.target.value) })}
            className="report-schedule__select"
          >
            {[1, 2, 3, 4, 5].map((hours) => (
              <option key={hours} value={hours}>Every {hours} hr{hours > 1 ? 's' : ''}</option>
            ))}
          </select>
        )}

        {showTime && (
          <input
            type="time"
            value={sched.send_time}
            onChange={(e) => onChange({ ...sched, send_time: e.target.value })}
            className="report-schedule__time"
          />
        )}

        {isWeekly && (
          <select
            value={sched.weekday}
            onChange={(e) => onChange({ ...sched, weekday: e.target.value })}
            className="report-schedule__select"
          >
            {WEEKDAYS.map((day) => (
              <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
            ))}
          </select>
        )}
      </div>

      <div className="report-schedule__row report-schedule__row--footer">
        {!isNew && sched.last_sent_at && (
          <span className="report-schedule__last">Last: {sched.last_sent_at.slice(0, 16)}</span>
        )}
        {!isNew ? (
          <button
            type="button"
            className="report-schedule__send"
            onClick={() => onSendNow(sched.id)}
            disabled={saving}
          >
            <Send size={11} /> Send
          </button>
        ) : (
          <button
            type="button"
            className="report-schedule__send"
            onClick={() => onChange({ ...sched, _save: true })}
            disabled={saving}
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
}

// Global "Report" trigger, sitting in the top nav (between History and Sign
// Out) instead of Dashboard.jsx's old always-visible "Email Schedules" bar.
// Lives at the shell level so it's reachable from every page - schedules
// persist in localStorage regardless of what's loaded on the Dashboard, so
// there's no need to tie this to Dashboard's own state anymore.
export default function ReportMenu() {
  const [open, setOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const menuRef = useRef(null);

  const loadSchedules = async () => {
    try {
      const response = await fetchSchedules();
      setSchedules(response.data?.schedules || []);
    } catch {
      // keep current state
    }
  };

  useEffect(() => {
    if (open) loadSchedules();
  }, [open]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleAddSchedule = () => {
    setSchedules((prev) => ([...prev, {
      recipient: '',
      frequency: 'daily',
      send_time: '09:00',
      weekday: 'monday',
      interval_hrs: 1,
      enabled: true,
      _new: true,
      _tempId: Date.now(),
    }]));
  };

  const handleSaveNewSchedule = async (sched) => {
    if (!sched.recipient || !sched.recipient.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSavingId(sched._tempId);
    try {
      await createSchedule({
        recipient: sched.recipient,
        frequency: sched.frequency,
        send_time: sched.send_time,
        weekday: sched.weekday,
        interval_hrs: sched.interval_hrs,
      });
      toast.success('Schedule saved');
      await loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSavingId(null);
    }
  };

  const handleUpdateSchedule = async (sched) => {
    if (!sched.id) return;
    setSavingId(sched.id);
    try {
      await updateSchedule(sched.id, {
        recipient: sched.recipient,
        frequency: sched.frequency,
        send_time: sched.send_time,
        weekday: sched.weekday,
        interval_hrs: sched.interval_hrs,
        enabled: sched.enabled,
      });
      await loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update schedule');
    } finally {
      setSavingId(null);
    }
  };

  const handleScheduleChange = (updated) => {
    if (updated._save) {
      handleSaveNewSchedule(updated);
      return;
    }
    if (!updated._new && updated.id) {
      handleUpdateSchedule(updated);
      return;
    }
    setSchedules((prev) => prev.map((item) => (
      item._tempId && item._tempId === updated._tempId ? updated : item
    )));
  };

  const handleDeleteSchedule = async (sched) => {
    if (sched._new) {
      setSchedules((prev) => prev.filter((item) => item._tempId !== sched._tempId));
      return;
    }
    try {
      await deleteSchedule(sched.id);
      toast('Schedule removed');
      await loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete');
    }
  };

  // No Dashboard state to pull date/camera/detections from up here anymore -
  // sendScheduleNow() already falls back to today/all-cameras/today's log
  // on its own when no context is passed.
  const handleSendScheduleNow = async (id) => {
    setSavingId(id);
    try {
      const response = await sendScheduleNow(id);
      if (response.data?.simulated) {
        toast.success(response.data.message || 'Simulated send (demo mode).');
      } else {
        toast.success(`Report emailed to ${response.data?.recipient}`);
      }
      await loadSchedules();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="report-menu" ref={menuRef}>
      <button type="button" className="nav-item report-menu__trigger" onClick={() => setOpen((prev) => !prev)}>
        <FileText size={14} />
        <span>Report</span>
      </button>

      {open && (
        <div className="report-menu__pop">
          <div className="report-menu__head">
            <strong>Email Schedules</strong>
            <span>Manual Send is real — scheduled/instant sends are simulated</span>
            <button type="button" className="report-menu__add" onClick={handleAddSchedule}>
              <Plus size={12} /> Add
            </button>
          </div>

          {schedules.length === 0 && (
            <p className="report-menu__empty">No schedules yet — click "Add" to create one.</p>
          )}

          <div className="report-menu__list">
            {schedules.map((sched) => (
              <ScheduleRow
                key={sched.id || sched._tempId}
                sched={sched}
                onChange={handleScheduleChange}
                onDelete={handleDeleteSchedule}
                onSendNow={handleSendScheduleNow}
                saving={savingId === (sched.id || sched._tempId)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
